import React, { useState, useEffect, useCallback } from "react";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import uuid from "react-uuid";
import Loading from "../../../common/components/loading/loading";
import { getQualityAttributes } from "../../../common/services/products/qualityAttributeService";
import SearchBox from "../../../common/components/search/searchBox";
import QualityAttributesTable from "./qualityAttributesTable";
import { useTranslation } from "react-i18next";

const QualityAttributes = ({ onClick, allowEdit }) => {
  const { t } = useTranslation(["products"]);
  const [qualityAttributes, setQualityAttributes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [sortColumn, setSortColumn] = useState({ path: "name", order: "asc" });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch quality attributes on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getQualityAttributes();
        setQualityAttributes(data);
      } catch (error) {
        console.error("Error fetching quality attributes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle quality attribute updates
  const handleQualityAttributeUpdated = useCallback((qualityAttribute) => {
    setQualityAttributes((prevAttributes) => {
      const updatedAttributes = [...prevAttributes];
      const index = updatedAttributes.findIndex(
        (a) => a.qualityAttributeId === qualityAttribute.qualityAttributeId
      );
      if (index > -1) {
        updatedAttributes.splice(index, 1);
      }
      updatedAttributes.push(qualityAttribute);
      return updatedAttributes;
    });
  }, []);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Handle sorting
  const handleSort = (sortEventOrColumn) => {
    // Handle modern AG Grid sort event
    if (sortEventOrColumn.api) {
      const { api } = sortEventOrColumn;
      const columnState = api.getColumnState();
      const sortedColumn = columnState.find((col) => col.sort);

      if (sortedColumn) {
        setSortColumn({
          path: sortedColumn.colId,
          order: sortedColumn.sort,
        });
      }
    } else {
      // Handle old sort format (for backward compatibility)
      setSortColumn(sortEventOrColumn);
    }
  };

  // Handle active toggle
  const handleToggleActiveOnly = () => {
    setActiveOnly(!activeOnly);
  };

  // Handle quality attribute selection
  const handleQualityAttributeSelected = (qualityAttribute, isNew = false) => {
    // Inject the callback function before calling back to the onClick event
    onClick(qualityAttribute, handleQualityAttributeUpdated, isNew);
  };

  // Get filtered and sorted data
  const getFilteredData = () => {
    let filtered = qualityAttributes;

    if (activeOnly) filtered = filtered.filter((a) => a.active);

    if (searchQuery)
      filtered = filtered.filter(
        (a) =>
          a.name != null &&
          a.name.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);

    return { totalCount: filtered.length, data: sorted };
  };

  if (isLoading) return <Loading />;

  const { totalCount, data: filteredQualityAttributes } = getFilteredData();

  const newQualityAttribute = {
    qualityAttributeId: uuid(),
    name: t("NewQualityAttribute"),
    active: true,
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          <SearchBox value={searchQuery} onChange={handleSearch} />
        </div>
        <div className="col">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("ActiveOnly")}
            checked={activeOnly}
            value={activeOnly}
            onChange={handleToggleActiveOnly}
          />
        </div>
        <div className="col">
          {allowEdit && (
            <button
              onClick={() =>
                handleQualityAttributeSelected(newQualityAttribute, true)
              }
              className="btn btn-sm btn-primary float-sm-right"
            >
              {t("AddQualityAttribute")}
            </button>
          )}
        </div>
      </div>
      <p>{t("RetrievedQualityAttribute", { count: totalCount })}</p>
      <QualityAttributesTable
        qualityAttributes={filteredQualityAttributes}
        sortColumn={sortColumn}
        onSort={handleSort}
        onRowClick={handleQualityAttributeSelected}
      />
    </div>
  );
};

export default QualityAttributes;
